import React, { useLayoutEffect, useMemo } from 'react';
import { Box, Grid, Preloader } from '@common-fe/common-fe';

import { Document } from '@/common/types';
import { RenderWrapperProps } from '@/components/elements/DocumentViewer/DocumentViewer.constants';
import useConvertTiffToPng from '@/utils/hooks/useConvertTiffToPng';

import DocumentPageWrapper from './DocumentPageWrapper';
import ImageViewer from './ImageViewer';

interface Props {
  receipt?: Document;
  zoom: number;
  rotate: number;
  renderWrapper?: (props: RenderWrapperProps) => React.ReactNode;
  isMultiPageViewMode?: boolean;
  setIsMultiPageViewMode?: (isMultiPageDocument: boolean) => void;
}

const TiffViewer: React.FC<Props> = ({
  receipt,
  zoom,
  rotate,
  renderWrapper,
  isMultiPageViewMode,
  setIsMultiPageViewMode,
}) => {
  const url = receipt?.blob;
  const { pngDocuments, isLoading } = useConvertTiffToPng({ url });
  const currentIsMultiPageViewMode = useMemo(
    () => zoom < 1 && isMultiPageViewMode,
    [zoom, isMultiPageViewMode]
  );
  const currentImageZoom = useMemo(() => (isMultiPageViewMode ? 1 : zoom), [isMultiPageViewMode, zoom]);

  useLayoutEffect(() => {
    if (zoom < 1) {
      setIsMultiPageViewMode?.(true);
    } else {
      setIsMultiPageViewMode?.(false);
    }
  }, [setIsMultiPageViewMode, zoom]);

  useLayoutEffect(() => {
    return () => {
      setIsMultiPageViewMode?.(false);
    };
  }, [setIsMultiPageViewMode, pngDocuments?.length]);

  if (isLoading) {
    return <Preloader testId="tiff-preloader" />;
  }

  return (
    <Grid
      data-testid={`tiff-viewer${currentIsMultiPageViewMode ? '_grid' : ''}`}
      columns={currentIsMultiPageViewMode ? '1/2' : '1fr'}
      gap={{ column: 'spacing48', row: 'spacing12' }}
    >
      {pngDocuments?.map((pngDocument, index) => {
        const content = currentIsMultiPageViewMode ? (
          <DocumentPageWrapper>
            <ImageViewer
              fileId={receipt?.fileId}
              rotate={rotate}
              index={index}
              zoom={currentImageZoom}
              receipt={pngDocument}
            />
          </DocumentPageWrapper>
        ) : (
          <ImageViewer
            index={index}
            fileId={receipt?.fileId}
            rotate={rotate}
            zoom={zoom}
            receipt={pngDocument}
          />
        );

        return (
          <Box key={index} margin={{ bottom: 'spacing12' }}>
            {renderWrapper
              ? renderWrapper({
                  index,
                  document: pngDocument,
                  content,
                  isMultiPageViewMode: currentIsMultiPageViewMode,
                  setIsMultiPageViewMode,
                })
              : content}
          </Box>
        );
      })}
    </Grid>
  );
};

export default TiffViewer;
